import React from "react";
import { Box, Typography } from "@mui/material";
import { glassPrescriptionHeaders } from "../../Assessment/EyeExamination/GlassPrescription";
import { lensPrescriptionHeaders } from "../../Assessment/EyeExamination/LensPrescription";
import { posteriorHeaders } from "../../Assessment/EyeExamination/PosteriorSegment";
import { anteriorSegmentHeaders } from "../../Assessment/EyeExamination/AnteriorSegment";
import { colorVisionHeaders } from "../../Assessment/EyeExamination/ColorVision";
import { fieldOfVisionHeaders } from "../../Assessment/EyeExamination/FieldOfVision";
import { autoRefractionHeaders } from "../../Assessment/EyeExamination/AutoRefraction";
import { refractionHeaders } from "../../Assessment/EyeExamination/Refraction";
import { subjectiveRefractionHeaders } from "../../Assessment/EyeExamination/SubjectiveRefraction";
import VisualAcuity from "../../Assessment/EyeExamination/VisualAcuity";
import EyeTableView, { Title } from "./EyeTableView";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";

function hasValue(test: any) {
  if (!test) return false;
  return Object.keys(test).some((row) => {
    if (!test[row]) return false;
    const containsValue = Object.keys(test[row]).some((col) => Boolean(test[row][col]));
    return containsValue;
  });
}

const sortColumnOrder = [
  "sph",
  "cyl",
  "axis",
  "dva",
  "add",
  "nva",
  "ipd",
  "pd",
  "prism",
  "eye",
  "rEye",
  "lEye",
  "both",
  "results"
];

const sortRowOrder = [
  "OD",
  "OS",
  "Distance",
  "Near",
  "Vitreous",
  "Fundus",
  "Lids",
  "Cornea",
  "Conjunctiva",
  "A/C",
  "Pupil/Iris",
  "Lens",
  "Horizontal Plane",
  "Vertial Plane"
];

const EyeAssessmentContent = ({
  eyeExaminationDoc
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eyeExaminationDoc: Record<string, any>;
}): React.JSX.Element => {
  const {
    vision: visionData,
    anteriorSegment: anteriorSegData,
    posteriorSegment: posteriorSegData,
    glassPrescription: glassPrescriptionData,
    lensPrescription: lensPrescriptionData
  } = eyeExaminationDoc;

  const resourceCentre = useCurrentResourceCentre();
  const eye = resourceCentre?.settings?.assessmentSettings?.showComponentsInPrint?.eye;

  const sortData = (data) =>
    Object.keys(data)
      .sort((a, b) => sortRowOrder.indexOf(a) - sortRowOrder.indexOf(b))
      .reduce((a, b) => {
        const sortedInside = Object.keys(data[b])
          .sort((ax, bx) => sortColumnOrder.indexOf(ax) - sortColumnOrder.indexOf(bx))
          .reduce((x, y) => {
            // eslint-disable-next-line no-param-reassign
            x[y] = data[b][y];
            return x;
          }, {});
        // eslint-disable-next-line no-param-reassign
        a[b] = sortedInside;
        return a;
      }, {});

  // show/hide booleans
  const showAided = hasValue(visionData.aided) && eye?.visualAcuity;
  const showUnaided = hasValue(visionData.unAided) && eye?.visualAcuity;
  const showPinHole = hasValue(visionData.pinHole) && eye?.visualAcuity;
  const showBestCorrectiveVisualAcuity =
    hasValue(visionData.bestCorrectiveVisualAcuity) && eye?.visualAcuity;

  const showRefraction = hasValue(visionData.refraction) && eye?.refraction;
  const showSubjectiveRefraction =
    hasValue(visionData.subjectiveRefraction) && (eye?.subjectiveRefraction ?? true);
  const showColorVision = hasValue(visionData.colorVision) && eye?.colorVision;
  const showFieldOfVision = hasValue(visionData.fieldOfVision) && eye?.fieldOfVision;
  const showAutoRefraction = hasValue(visionData.autoRefraction) && eye?.autoRefraction;

  const showAnteriorSegment = hasValue(anteriorSegData) && eye?.anteriorSegment;
  const showPosteriorSegment = hasValue(posteriorSegData) && eye?.posteriorSegment;
  const showGlassPrescription = hasValue(glassPrescriptionData) && (eye?.glassPrescription ?? true);
  const showLensPrescription = hasValue(lensPrescriptionData) && (eye?.lensPrescription ?? true);

  // sorted data
  const sortedRefractionData = sortData(visionData.refraction);
  const sortedSubjectiveRefractionData = visionData.subjectiveRefraction
    ? sortData(visionData.subjectiveRefraction)
    : {};
  const sortedColorVisionData = sortData(visionData.colorVision);
  const sortedFieldOfVisionData = sortData(visionData.fieldOfVision);
  const sortedAutoRefractionData = sortData(visionData.autoRefraction);

  const sortedAnteriorData = sortData(anteriorSegData);
  const sortedPosteriorsegmentData = sortData(posteriorSegData);
  const sortedGlassPrescriptionData = glassPrescriptionData ? sortData(glassPrescriptionData) : {};
  const sortedLensPrescriptionData = lensPrescriptionData ? sortData(lensPrescriptionData) : {};

  return (
    <Box>
      {(showAutoRefraction ||
        showRefraction ||
        showSubjectiveRefraction ||
        showUnaided ||
        showAided ||
        showBestCorrectiveVisualAcuity ||
        showPinHole ||
        showFieldOfVision ||
        showColorVision) && <Title label="Vision" />}

      {showAutoRefraction && (
        <Box sx={{ mb: 2 }}>
          <Title label="Auto Refraction" />
          <EyeTableView headers={autoRefractionHeaders} data={sortedAutoRefractionData} />
        </Box>
      )}
      {showRefraction && (
        <Box sx={{ mb: 2 }}>
          <Title label="Refraction" />
          <EyeTableView headers={refractionHeaders} data={sortedRefractionData} />
        </Box>
      )}
      {showSubjectiveRefraction && (
        <Box sx={{ mb: 2 }}>
          <Title label="Subjective Refraction" />
          <EyeTableView
            headers={subjectiveRefractionHeaders}
            data={sortedSubjectiveRefractionData}
          />
        </Box>
      )}
      {(showUnaided || showAided || showBestCorrectiveVisualAcuity || showPinHole) && (
        <Box sx={{ mb: 2 }}>
          <Title label="Visual Acuity" />
          <VisualAcuity visionData={eyeExaminationDoc} readOnly />
        </Box>
      )}
      {showFieldOfVision && (
        <Box sx={{ mb: 2 }}>
          <Title label="Field of Vision" />
          <EyeTableView headers={fieldOfVisionHeaders} data={sortedFieldOfVisionData} />
        </Box>
      )}
      {showColorVision && (
        <Box sx={{ mb: 2 }}>
          <Title label="Color Vision" />
          <EyeTableView headers={colorVisionHeaders} data={sortedColorVisionData} />
        </Box>
      )}
      {showAnteriorSegment && (
        <Box sx={{ mb: 2 }}>
          <Title label="Anterior Segment" />
          <EyeTableView headers={anteriorSegmentHeaders} data={sortedAnteriorData} />
        </Box>
      )}
      {showPosteriorSegment && (
        <Box sx={{ mb: 2 }}>
          <Title label="Posterior Segment" />
          <EyeTableView headers={posteriorHeaders} data={sortedPosteriorsegmentData} />
        </Box>
      )}
      {showGlassPrescription && (
        <Box sx={{ mb: 2 }}>
          <Title label="Glass Prescription" />
          <EyeTableView headers={glassPrescriptionHeaders} data={sortedGlassPrescriptionData} />
          {eyeExaminationDoc.remarks?.glassPrescription && (
            <Typography mt={1}>Remarks: {eyeExaminationDoc.remarks?.glassPrescription}</Typography>
          )}
        </Box>
      )}
      {showLensPrescription && (
        <Box sx={{ mb: 2 }}>
          <Title label="Lens Prescription" />
          <EyeTableView headers={lensPrescriptionHeaders} data={sortedLensPrescriptionData} />
          {eyeExaminationDoc.remarks?.lensPrescription && (
            <Typography mt={1}>Remarks: {eyeExaminationDoc.remarks?.lensPrescription}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EyeAssessmentContent;
